/**
 * AudioBus v2 (WAVE3 G13) — flagi music/sfx + odtwarzanie SFX z plików ogg.
 * Muzyka (MusicManager) konsumuje subscribe() w następnym tasku (G14).
 */

export type AudioFlags = {
  musicOn: boolean;
  sfxOn: boolean;
};

export type AudioListener = (flags: AudioFlags) => void;

export type PlaySfxOpts = {
  /** 0..1, domyślnie 1 */
  volume?: number;
};

const SFX_BASE = '/assets/audio/sfx';
const POOL_SIZE = 6;

/** Znane klucze sampli (fetch.mjs). Inne klucze też spróbują pliku o tej nazwie. */
export const SFX_KEYS = [
  'sfx_click',
  'sfx_buy',
  'sfx_success',
  'sfx_fail_soft',
  'sfx_splash',
  'sfx_slide',
  'sfx_pet',
  'sfx_bell',
  'sfx_build',
  'sfx_reward',
  'sfx_wrong',
  'sfx_cheer',
  'sfx_spin',
] as const;

export type SfxKey = (typeof SFX_KEYS)[number] | string;

function sfxUrl(key: string): string {
  const id = key.startsWith('sfx_') ? key : `sfx_${key}`;
  return `${SFX_BASE}/${id}.ogg`;
}

export class AudioBus {
  musicOn = true;
  sfxOn = true;
  private unlocked = false;
  private listeners = new Set<AudioListener>();
  /** Pula elementów per klucz — overlap OK (kilka równoległych odtworzeń). */
  private pools = new Map<string, HTMLAudioElement[]>();

  unlock(): void {
    this.unlocked = true;
  }

  setMusic(on: boolean): void {
    if (this.musicOn === on) return;
    this.musicOn = on;
    this.persist();
    this.emit();
  }

  setSfx(on: boolean): void {
    if (this.sfxOn === on) return;
    this.sfxOn = on;
    this.persist();
    this.emit();
  }

  /**
   * Subskrypcja zmian flag (musicOn / sfxOn).
   * Natychmiast woła listener z aktualnym stanem.
   * Zwraca unsubscribe.
   */
  subscribe(listener: AudioListener): () => void {
    this.listeners.add(listener);
    listener({ musicOn: this.musicOn, sfxOn: this.sfxOn });
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    const flags: AudioFlags = { musicOn: this.musicOn, sfxOn: this.sfxOn };
    for (const l of [...this.listeners]) {
      try {
        l(flags);
      } catch {
        /* ignore listener errors */
      }
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(
        'pieskowo.audio',
        JSON.stringify({ musicOn: this.musicOn, sfxOn: this.sfxOn }),
      );
    } catch {
      /* ignore */
    }
  }

  load(): void {
    try {
      const raw = localStorage.getItem('pieskowo.audio');
      if (!raw) return;
      const v = JSON.parse(raw) as { musicOn?: boolean; sfxOn?: boolean };
      if (typeof v.musicOn === 'boolean') this.musicOn = v.musicOn;
      if (typeof v.sfxOn === 'boolean') this.sfxOn = v.sfxOn;
    } catch {
      /* ignore */
    }
  }

  get isUnlocked(): boolean {
    return this.unlocked;
  }

  /**
   * Odtwórz sample SFX (HTMLAudioElement, pula, overlap OK).
   * No-op gdy sfxOn=false lub brak window/Audio.
   */
  playSfx(key: SfxKey, opts?: PlaySfxOpts): void {
    if (!this.sfxOn) return;
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;

    const volume = clamp01(opts?.volume ?? 1);
    if (volume <= 0) return;

    const id = String(key);
    try {
      const el = this.acquire(id);
      el.volume = volume;
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          /* autoplay policy — ignore */
        });
      }
      this.unlocked = true;
    } catch {
      /* ignore */
    }
  }

  private acquire(key: string): HTMLAudioElement {
    let pool = this.pools.get(key);
    if (!pool) {
      pool = [];
      this.pools.set(key, pool);
    }

    // wolny element (zakończony / nieodtwarzany)
    for (const el of pool) {
      if (el.paused || el.ended) return el;
    }

    if (pool.length < POOL_SIZE) {
      const el = new Audio(sfxUrl(key));
      el.preload = 'auto';
      pool.push(el);
      return el;
    }

    // wszystkie zajęte — restart najstarszego
    const el = pool[0]!;
    try {
      el.pause();
    } catch {
      /* ignore */
    }
    return el;
  }

  /** Reset puli (testy). */
  resetPools(): void {
    for (const pool of this.pools.values()) {
      for (const el of pool) {
        try {
          el.pause();
        } catch {
          /* ignore */
        }
      }
    }
    this.pools.clear();
  }
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 1;
  return Math.min(1, Math.max(0, v));
}

export const audioBus = new AudioBus();
